/**
 * useCampaignBuilder Hook
 *
 * State management for the campaign builder. Holds nodes, template metadata,
 * CRUD operations, persistence, AND n8n-style testing state.
 *
 * @param {string|null} templateId - Existing template to load, or null for new
 * @param {string|null} userId     - Effective owner UID: new templates get
 *                                   created_by=userId and the auto-subscribe
 *                                   targets this user (supports impersonation)
 * @param {object}  options
 * @param {string}  options.ownershipType - 'company' | 'user' for NEW templates.
 *                                          Read at save-time via a ref so late
 *                                          selector changes (or FutureBot-driven
 *                                          saves) always use the current value.
 * @param {string}  options.executionScope - 'user' (default) | 'company' for NEW
 *                                          templates. 'company' creates a Company
 *                                          Automation: hidden (is_visible=false),
 *                                          runner subscription created AFTER nodes
 *                                          save (so it clones the real workflow)
 *                                          and starts disabled.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  fetchTemplateById,
  createTemplate,
  updateTemplate,
  replaceTemplateNodes,
  transformTemplateToCamelCase,
  transformNodeToCamelCase,
} from '../api/supabase/automations';
import { remapCampaignNodes } from '../utils/campaignNodeRemap';

const fbFunctions = getFunctions(undefined, 'us-east1');

function generateId() {
  return crypto.randomUUID();
}

function buildDefaultConfig(subType) {
  switch (subType) {
    case 'SEND_EMAIL':
      return { emailField: 'email', fromEmail: '', replyTo: '', subject: '', body: '' };
    case 'SEND_SMS':
      return { message: '', media: null };
    case 'SEND_POSTCARD':
      return { designId: null, designName: '', designFields: [], mailClass: 'FirstClass', addressSource: 'mailing', designVariables: {} };
    case 'WAIT':
      return { duration: 1, unit: 'days' };
    case 'WAIT_FOR':
      return { field: '', operator: 'equals', value: '', checkInterval: 60, timeoutDuration: 30, timeoutUnit: 'days' };
    case 'WAIT_UNTIL_MEETING':
      return { duration: 24, unit: 'hours', direction: 'before' };
    case 'IF_ELSE':
      return { field: '', operator: 'exists', value: '' };
    case 'ADD_NOTE':
      return { text: '' };
    case 'UPDATE_STAGE':
      return { stage: '' };
    case 'CREATE_TASK':
      return { title: '', description: '', priority: 'medium', dueDays: 1 };
    case 'UPDATE_DEAL_FIELD':
      return { fieldName: '', fieldValue: '' };
    case 'MANAGE_TAG':
      return { action: 'add', tagName: '', triggerCampaigns: false };
    case 'SEND_WEBHOOK':
      return { url: '', method: 'POST', body: '' };
    case 'APPEND_SHEET_ROW':
      return { spreadsheetId: '', sheetName: '', columns: [{ label: '', value: '' }] };
    case 'SEND_NOTIFICATION':
      return { message: '', notifyOwner: true };
    case 'ENROLL_CAMPAIGN':
      return { targetCampaignId: '', ifAlreadyEnrolled: 'skip' };
    case 'UNENROLL_CAMPAIGN':
      return { targetCampaignId: '' };
    case 'SWITCH':
      return { field: '', cases: [] };
    case 'AB_SPLIT':
      return { splitPercent: 50 };
    default:
      return {};
  }
}

const NODE_TYPE_MAP = {
  SEND_EMAIL: 'ACTION', SEND_SMS: 'ACTION', SEND_POSTCARD: 'ACTION', ADD_NOTE: 'ACTION',
  UPDATE_STAGE: 'ACTION', CREATE_TASK: 'ACTION', UPDATE_DEAL_FIELD: 'ACTION',
  MANAGE_TAG: 'ACTION', SEND_WEBHOOK: 'ACTION', SEND_NOTIFICATION: 'ACTION',
  APPEND_SHEET_ROW: 'ACTION', ENROLL_CAMPAIGN: 'ACTION', UNENROLL_CAMPAIGN: 'ACTION',
  WAIT: 'WAIT', WAIT_FOR: 'WAIT', WAIT_UNTIL_MEETING: 'WAIT',
  IF_ELSE: 'CONDITION', SWITCH: 'CONDITION', AB_SPLIT: 'CONDITION',
  CONTACT_CREATED: 'TRIGGER', CONTACT_UPDATED: 'TRIGGER', TAG_ADDED: 'TRIGGER',
  TAG_REMOVED: 'TRIGGER', DEAL_CREATED: 'TRIGGER', PIPELINE_STAGE_CHANGED: 'TRIGGER',
  DEAL_FIELD_UPDATED: 'TRIGGER', DEAL_ASSIGNED: 'TRIGGER', DEAL_STALLED: 'TRIGGER',
  EMAIL_OPENED: 'TRIGGER', EMAIL_REPLIED: 'TRIGGER', SMS_REPLIED: 'TRIGGER',
  WEBHOOK_RECEIVED: 'TRIGGER', MANUAL_ADD: 'TRIGGER', MANUAL_BULK_ADD: 'TRIGGER',
  SCHEDULE_RECURRING: 'TRIGGER', SCHEDULE_DATE_FIELD: 'TRIGGER',
  MEETING_BOOKED: 'TRIGGER', MEETING_RESCHEDULED: 'TRIGGER',
  MEETING_CANCELLED: 'TRIGGER', MEETING_NO_SHOW: 'TRIGGER',
};

/**
 * Collects all node IDs reachable via falseNodeId chains from every
 * IF_ELSE / AB_SPLIT node in the list.
 */
function collectFalseBranchIds(nodes) {
  const nodeMap = {};
  nodes.forEach(n => { nodeMap[n.id] = n; });
  const falseBranchIds = new Set();

  nodes.forEach(n => {
    if ((n.subType === 'IF_ELSE' || n.subType === 'AB_SPLIT' || n.subType === 'WAIT_FOR') && n.falseNodeId) {
      let currentId = n.falseNodeId;
      while (currentId && nodeMap[currentId] && !falseBranchIds.has(currentId)) {
        falseBranchIds.add(currentId);
        currentId = nodeMap[currentId].nextNodeId;
      }
    }
  });

  return falseBranchIds;
}

/**
 * Branch-aware relinking. Relinks the main trunk (nodes NOT in any false branch)
 * by array order, but leaves false-branch chains and falseNodeId pointers untouched.
 */
function relinkNodes(nodes) {
  const falseBranchIds = collectFalseBranchIds(nodes);
  const mainTrunk = nodes.filter(n => !falseBranchIds.has(n.id));

  const relinked = nodes.map(n => {
    if (falseBranchIds.has(n.id)) return n;
    const trunkIdx = mainTrunk.findIndex(m => m.id === n.id);
    return {
      ...n,
      nextNodeId: trunkIdx < mainTrunk.length - 1 ? mainTrunk[trunkIdx + 1].id : null,
      orderIndex: nodes.indexOf(n),
    };
  });

  return relinked;
}

export function useCampaignBuilder(templateId = null, userId = null, { ownershipType = 'company', executionScope = 'user' } = {}) {
  const [template, setTemplate] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(!!templateId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const initialLoadRef = useRef(false);

  const ownershipTypeRef = useRef(ownershipType);
  useEffect(() => { ownershipTypeRef.current = ownershipType; }, [ownershipType]);

  const executionScopeRef = useRef(executionScope);
  useEffect(() => { executionScopeRef.current = executionScope; }, [executionScope]);

  // -----------------------------------------------------------------------
  // n8n-style test state
  // -----------------------------------------------------------------------
  const [testResults, setTestResults] = useState({});
  const [testContact, setTestContact] = useState(null);
  const [testRunning, setTestRunning] = useState(null);
  const [triggerTestResult, setTriggerTestResult] = useState(null);
  const [fullTestResult, setFullTestResult] = useState(null);

  // Load existing template
  useEffect(() => {
    if (!templateId || initialLoadRef.current) return;
    initialLoadRef.current = true;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const raw = await fetchTemplateById(templateId);
        const tmpl = transformTemplateToCamelCase(raw);
        setTemplate(tmpl);
        setNodes(
          (tmpl.nodes || [])
            .sort((a, b) => a.orderIndex - b.orderIndex)
        );
      } catch (err) {
        console.error('[useCampaignBuilder] load error:', err);
        setError(err.message || 'Failed to load template');
      } finally {
        setLoading(false);
      }
    })();
  }, [templateId]);

  // -----------------------------------------------------------------------
  // Template metadata
  // -----------------------------------------------------------------------
  const setTemplateMeta = useCallback((updates) => {
    setTemplate((prev) => ({ ...prev, ...updates }));
    setIsDirty(true);
  }, []);

  // -----------------------------------------------------------------------
  // Node operations
  // -----------------------------------------------------------------------
  const addNode = useCallback((subType, afterIndex = null, { branch = 'true', parentIfElseId = null } = {}) => {
    const nodeType = NODE_TYPE_MAP[subType] || 'ACTION';
    const newNode = {
      id: generateId(),
      nodeType,
      subType,
      config: buildDefaultConfig(subType),
      nextNodeId: null,
      falseNodeId: null,
      orderIndex: 0,
    };

    if (branch === 'false' && parentIfElseId) {
      // Insert into the false branch of an IF_ELSE node
      setNodes((prev) => {
        let list = prev.map(n => ({ ...n }));
        const parentIdx = list.findIndex(n => n.id === parentIfElseId);
        if (parentIdx === -1) return relinkNodes([...list, newNode]);

        if (!list[parentIdx].falseNodeId) {
          list[parentIdx] = { ...list[parentIdx], falseNodeId: newNode.id };
          list.push(newNode);
        } else {
          let lastInChain = list[parentIdx].falseNodeId;
          const nodeMap = {};
          list.forEach(n => { nodeMap[n.id] = n; });
          while (nodeMap[lastInChain]?.nextNodeId && nodeMap[nodeMap[lastInChain].nextNodeId]) {
            lastInChain = nodeMap[lastInChain].nextNodeId;
          }
          const lastIdx = list.findIndex(n => n.id === lastInChain);
          if (lastIdx !== -1) {
            list[lastIdx] = { ...list[lastIdx], nextNodeId: newNode.id };
          }
          list.push(newNode);
        }

        return relinkNodes(list);
      });
    } else {
      // Standard insertion into the main trunk
      setNodes((prev) => {
        const list = [...prev];
        const insertAt = afterIndex !== null ? afterIndex + 1 : list.length;
        list.splice(insertAt, 0, newNode);
        return relinkNodes(list);
      });
    }

    setIsDirty(true);
    return newNode.id;
  }, []);

  const removeNode = useCallback((nodeId) => {
    setNodes((prev) => {
      const list = [...prev];
      const nodeToRemove = list.find(n => n.id === nodeId);
      if (!nodeToRemove) return relinkNodes(list.filter(n => n.id !== nodeId));

      // Check if this node is the first in a false branch
      const parentIfElse = list.find(n =>
        (n.subType === 'IF_ELSE' || n.subType === 'AB_SPLIT' || n.subType === 'WAIT_FOR') && n.falseNodeId === nodeId
      );

      if (parentIfElse) {
        parentIfElse.falseNodeId = nodeToRemove.nextNodeId || null;
      }

      return relinkNodes(list.filter(n => n.id !== nodeId));
    });
    setIsDirty(true);
  }, []);

  const updateNodeConfig = useCallback((nodeId, configUpdates) => {
    setNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId ? { ...n, config: { ...n.config, ...configUpdates } } : n
      )
    );
    setIsDirty(true);
  }, []);

  const updateNodeSubType = useCallback((nodeId, subType) => {
    const nodeType = NODE_TYPE_MAP[subType] || 'ACTION';
    setNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId ? { ...n, subType, nodeType, config: buildDefaultConfig(subType) } : n
      )
    );
    setIsDirty(true);
  }, []);

  const moveNode = useCallback((fromIndex, toIndex) => {
    setNodes((prev) => {
      const list = [...prev];
      const [moved] = list.splice(fromIndex, 1);
      list.splice(toIndex, 0, moved);
      return relinkNodes(list);
    });
    setIsDirty(true);
  }, []);

  const setFalseNodeId = useCallback((nodeId, falseNodeId) => {
    setNodes((prev) =>
      prev.map((n) => n.id === nodeId ? { ...n, falseNodeId } : n)
    );
    setIsDirty(true);
  }, []);

  // -----------------------------------------------------------------------
  // Import — load a previously exported campaign (paste or file)
  // -----------------------------------------------------------------------
  /**
   * Applies a parsed export object ({ template, nodes }) to the builder.
   *
   * Every node receives a freshly generated UUID and ALL pointers are remapped
   * through that map (nextNodeId, falseNodeId, and SWITCH config.cases[].nextNodeId
   * + config.defaultNextNodeId). This is required: replaceTemplateNodes upserts on
   * the node id, so reusing the exported ids would re-point the SOURCE campaign's
   * rows to this template on save and corrupt the original. targetCampaignId on
   * ENROLL/UNENROLL is intentionally left untouched (it references another template).
   *
   * @param {object} data - parsed export payload
   * @returns {{ nodeCount: number }}
   * @throws {Error} when the payload is malformed or contains unsupported node types
   */
  const importCampaign = useCallback((data) => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      throw new Error('Invalid campaign JSON: expected an object with "template" and "nodes".');
    }
    const incoming = Array.isArray(data.nodes) ? data.nodes : [];

    // Validate every node sub_type is a known, executable type (not a trigger)
    const bad = incoming.filter(
      (n) => !n?.subType || !NODE_TYPE_MAP[n.subType] || NODE_TYPE_MAP[n.subType] === 'TRIGGER'
    );
    if (bad.length) {
      const names = [...new Set(bad.map((n) => n?.subType || 'undefined'))].join(', ');
      throw new Error(`Unsupported node type(s): ${names}`);
    }

    // Fresh ids + remapped pointers (shared with duplicateTemplate). nodeType
    // is recomputed from the sub_type map rather than trusted from the payload.
    const newNodes = remapCampaignNodes(
      incoming.map((n) => ({ ...n, nodeType: NODE_TYPE_MAP[n.subType] || 'ACTION' })),
      generateId
    );

    // 3) Apply template metadata (keep existing template.id if editing; a new campaign has none)
    const t = data.template || {};
    setTemplate((prev) => ({
      ...(prev || {}),
      name: t.name ?? prev?.name ?? '',
      description: t.description ?? prev?.description ?? '',
      triggerType: t.triggerType || prev?.triggerType || 'MANUAL_ADD',
      triggerConfig: t.triggerConfig || {},
      enrollmentRules: t.enrollmentRules || prev?.enrollmentRules || {},
    }));
    setNodes(newNodes);
    setIsDirty(true);
    return { nodeCount: newNodes.length };
  }, []);

  // -----------------------------------------------------------------------
  // Persistence
  // -----------------------------------------------------------------------
  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      let tmpl = template;
      let isNew = false;
      let subscribeResult = null;
      // Company Automation: hidden admin-only template that runs on any
      // owner's records via a single runner subscription.
      const isAutomation = executionScopeRef.current === 'company';
      if (!tmpl?.id) {
        isNew = true;
        const created = await createTemplate({
          name: tmpl?.name || 'Untitled Campaign',
          description: tmpl?.description || '',
          triggerType: tmpl?.triggerType || 'MANUAL_ADD',
          triggerConfig: tmpl?.triggerConfig || {},
          createdBy: userId,
          ownershipType: isAutomation || ownershipTypeRef.current !== 'user' ? 'company' : 'user',
          ...(isAutomation ? { executionScope: 'company', isVisible: false } : {}),
        });
        tmpl = transformTemplateToCamelCase(created);
        setTemplate(tmpl);
      } else {
        const updated = await updateTemplate(tmpl.id, {
          name: tmpl.name, description: tmpl.description,
          triggerType: tmpl.triggerType, triggerConfig: tmpl.triggerConfig,
          enrollmentRules: tmpl.enrollmentRules,
          version: (tmpl.version || 1) + 1,
        });
        tmpl = transformTemplateToCamelCase(updated);
        setTemplate(tmpl);
      }
      const savedNodes = await replaceTemplateNodes(tmpl.id, nodes);
      setNodes(
        (savedNodes || []).map(transformNodeToCamelCase).sort((a, b) => a.orderIndex - b.orderIndex)
      );

      if (isNew) {
        // Subscribe AFTER the nodes save so the clone picks up the real
        // workflow — subscribeToCampaign snapshots the template's current
        // nodes, and syncCampaignUpdates skips subscriptions whose
        // template_version already matches, so an early empty clone would
        // never heal. Applies to the creator's auto-subscription and to
        // Automation runners alike (the backend creates runners disabled).
        // targetUserId keeps the subscription on the effective owner — when an
        // admin builds while impersonating, the impersonated user gets it (the
        // backend skips its admin check when target === caller).
        try {
          const subscribeFn = httpsCallable(fbFunctions, 'subscribeToCampaign');
          const res = await subscribeFn(
            userId ? { templateId: tmpl.id, targetUserId: userId } : { templateId: tmpl.id }
          );
          subscribeResult = res.data;
        } catch (subErr) {
          console.warn('[useCampaignBuilder] Auto-subscribe failed:', subErr.message);
        }
      }

      if (templateId) {
        try {
          const syncFn = httpsCallable(fbFunctions, 'syncCampaignUpdates');
          await syncFn({ templateId: tmpl.id });
        } catch (syncErr) {
          console.warn('[useCampaignBuilder] Sync to subscribers failed:', syncErr.message);
        }
      }

      setIsDirty(false);
      if (isNew) {
        return { ...tmpl, _isNew: true, _userCampaignId: subscribeResult?.userCampaignId };
      }
      return tmpl;
    } catch (err) {
      console.error('[useCampaignBuilder] save error:', err);
      setError(err.message || 'Failed to save campaign');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [template, nodes, templateId, userId]);

  // -----------------------------------------------------------------------
  // Testing — Trigger
  // -----------------------------------------------------------------------
  const testTrigger = useCallback(async () => {
    if (!template?.triggerType) return;
    setTestRunning('trigger');
    setTriggerTestResult(null);
    try {
      const fn = httpsCallable(fbFunctions, 'testTriggerMatch');
      const result = await fn({
        triggerType: template.triggerType,
        triggerConfig: template.triggerConfig || {},
      });
      setTriggerTestResult(result.data);
    } catch (err) {
      console.error('[useCampaignBuilder] testTrigger error:', err);
      setTriggerTestResult({ success: false, error: err.message });
    } finally {
      setTestRunning(null);
    }
  }, [template]);

  // -----------------------------------------------------------------------
  // Testing — Single Node
  // -----------------------------------------------------------------------
  const testNode = useCallback(async (nodeId) => {
    if (!testContact?.id) return;
    setTestRunning(nodeId);

    try {
      const node = nodes.find(n => n.id === nodeId);
      if (!node) return;

      // Build accumulated prior context from previously tested nodes
      const priorNodes = {};
      const nodeIndex = nodes.findIndex(n => n.id === nodeId);
      for (let i = 0; i < nodeIndex; i++) {
        const prevId = nodes[i].id;
        if (testResults[prevId]?.output) {
          priorNodes[prevId] = { type: nodes[i].subType, output: testResults[prevId].output.details || {} };
        }
      }

      const fn = httpsCallable(fbFunctions, 'testSingleNode');
      const result = await fn({
        contactId: testContact.id,
        node: { subType: node.subType, config: node.config },
        priorContext: triggerTestResult?.sampleContext
          ? { ...triggerTestResult.sampleContext, nodes: priorNodes }
          : { nodes: priorNodes },
      });

      setTestResults(prev => ({
        ...prev,
        [nodeId]: result.data,
      }));
    } catch (err) {
      console.error('[useCampaignBuilder] testNode error:', err);
      setTestResults(prev => ({
        ...prev,
        [nodeId]: { success: false, status: 'error', output: { summary: err.message, details: { error: err.message } } },
      }));
    } finally {
      setTestRunning(null);
    }
  }, [testContact, nodes, testResults, triggerTestResult]);

  // -----------------------------------------------------------------------
  // Testing — Full Run
  // -----------------------------------------------------------------------
  const testFullRun = useCallback(async () => {
    if (!testContact?.id || !nodes.length) return;
    setTestRunning('full');
    setFullTestResult(null);
    setTestResults({});

    try {
      const fn = httpsCallable(fbFunctions, 'simulateCampaign');
      const result = await fn({
        contactId: testContact.id,
        rawNodes: nodes,
        triggerType: template?.triggerType,
      });

      const data = result.data;
      setFullTestResult(data);

      // Populate test results per node with a stagger for visual effect
      const stepsByNode = {};
      (data.steps || []).forEach(step => {
        stepsByNode[step.nodeId] = {
          success: true,
          status: step.status || 'success',
          input: step.input,
          output: step.output,
          nextPath: step.nextPath,
          timing: { durationMs: 0 },
        };
      });

      // Stagger the population
      const nodeIds = (data.steps || []).map(s => s.nodeId);
      for (let i = 0; i < nodeIds.length; i++) {
        await new Promise(r => setTimeout(r, 200));
        setTestResults(prev => ({ ...prev, [nodeIds[i]]: stepsByNode[nodeIds[i]] }));
      }
    } catch (err) {
      console.error('[useCampaignBuilder] testFullRun error:', err);
      setFullTestResult({ success: false, error: err.message });
    } finally {
      setTestRunning(null);
    }
  }, [testContact, nodes, template]);

  // -----------------------------------------------------------------------
  // Execution — Real execute of a single node
  // -----------------------------------------------------------------------
  const executeNode = useCallback(async (nodeId) => {
    if (!testContact?.id) return;
    setTestRunning(nodeId);

    try {
      const node = nodes.find(n => n.id === nodeId);
      if (!node) return;

      const priorNodes = {};
      const nodeIndex = nodes.findIndex(n => n.id === nodeId);
      for (let i = 0; i < nodeIndex; i++) {
        const prevId = nodes[i].id;
        if (testResults[prevId]?.output) {
          priorNodes[prevId] = { type: nodes[i].subType, output: testResults[prevId].output.details || {} };
        }
      }

      const fn = httpsCallable(fbFunctions, 'executeSingleNode');
      const result = await fn({
        contactId: testContact.id,
        node: { subType: node.subType, config: node.config, nodeType: node.nodeType },
        priorContext: triggerTestResult?.sampleContext
          ? { ...triggerTestResult.sampleContext, nodes: priorNodes }
          : { nodes: priorNodes },
      });

      setTestResults(prev => ({
        ...prev,
        [nodeId]: { ...result.data, mode: 'execute' },
      }));
    } catch (err) {
      console.error('[useCampaignBuilder] executeNode error:', err);
      setTestResults(prev => ({
        ...prev,
        [nodeId]: { success: false, status: 'error', mode: 'execute', output: { summary: err.message, details: { error: err.message } } },
      }));
    } finally {
      setTestRunning(null);
    }
  }, [testContact, nodes, testResults, triggerTestResult]);

  // -----------------------------------------------------------------------
  // Testing — Clear
  // -----------------------------------------------------------------------
  const clearTestResults = useCallback(() => {
    setTestResults({});
    setTriggerTestResult(null);
    setFullTestResult(null);
    setTestRunning(null);
  }, []);

  return {
    template, nodes, loading, saving, error, isDirty,
    setTemplateMeta, addNode, removeNode, updateNodeConfig,
    updateNodeSubType, moveNode, setFalseNodeId, save, importCampaign,
    // Test state
    testResults, testContact, testRunning, triggerTestResult, fullTestResult,
    // Test + Execute actions
    setTestContact, testTrigger, testNode, testFullRun, clearTestResults, executeNode,
  };
}
